import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../../../constants/config";

const DEFAULT_NEW_VENDOR = {
  name: "", businessType: "", otherBusinessType: "",
  contactDetails: { phone: "", email: "", address: "" },
  gstId: "",
  bankDetails: { accountName: "", accountNumber: "", ifscCode: "", bankName: "" },
  contractExpiry: "", status: "active", agreementFile: "", transactions: [],
  performance: { onTimeDelivery: 0, qualityRating: 0, responseTime: "" },
};

const DEFAULT_NEW_PRODUCT = {
  vendorId: "", productName: "", brand: "", quantity: 0,
  mrp: 0, costPrice: 0, buyDate: "", paymentDate: "", paymentStatus: "pending",
};

const DEFAULT_NEW_TRANSACTION = {
  date: new Date().toISOString().split("T")[0],
  amount: 0, paid_amount: 0, status: "pending", deliveryOnTime: true, notes: "",
};

export const BUSINESS_TYPES = [
  "Uniforms & Dress", "Laboratory Equipment", "Stationery", "IT Equipment",
  "Maintenance", "Transport", "Canteen Supplies", "Sports Equipment",
  "Books & Publications", "Furniture", "Other",
];

const validateVendor = (vendor) => {
  const errors = {};
  if (!vendor.name?.trim()) errors.name = "Vendor name is required";
  else if (vendor.name.trim().length < 2) errors.name = "Vendor name must be at least 2 characters";
  if (!vendor.businessType || vendor.businessType === "empty") errors.businessType = "Business type is required";
  else if (vendor.businessType === "Other") errors.businessType = "Please specify your business type";
  const phone = vendor.contactDetails?.phone?.trim();
  if (phone && !/^[0-9]{10}$/.test(phone.replace(/\D/g, ""))) errors.phone = "Phone must be 10 digits";
  const email = vendor.contactDetails?.email?.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Please enter a valid email";
  if (vendor.gstId?.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(vendor.gstId.trim()))
    errors.gstId = "Invalid GST ID format (e.g. 22AAAAA0000A1Z5)";
  const bank = vendor.bankDetails || {};
  if (bank.accountName?.trim()) {
    if (!/^[a-zA-Z\s]+$/.test(bank.accountName.trim())) errors.accountName = "Account name should only contain letters";
    else if (bank.accountName.trim().length < 2) errors.accountName = "Account name must be at least 2 characters";
  }
  if (bank.accountNumber?.trim()) {
    if (!/^\d+$/.test(bank.accountNumber.trim())) errors.accountNumber = "Account number should only contain digits";
    else if (bank.accountNumber.trim().length < 9) errors.accountNumber = "Account number must be at least 9 digits";
    else if (bank.accountNumber.trim().length > 18) errors.accountNumber = "Account number cannot exceed 18 digits";
  }
  if (bank.ifscCode?.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(bank.ifscCode.trim()))
    errors.ifscCode = "Invalid IFSC code (e.g. SBIN0001234)";
  if (bank.bankName?.trim()) {
    if (!/^[a-zA-Z\s&]+$/.test(bank.bankName.trim())) errors.bankName = "Bank name should only contain letters";
    else if (bank.bankName.trim().length < 2) errors.bankName = "Bank name must be at least 2 characters";
  }
  return errors;
};

export function useVendors() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [currentView, setCurrentView] = useState("vendors");
  const [filterOptions, setFilterOptions] = useState({ businessType: "all", status: "all" });
  const [editingVendor, setEditingVendor] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [productSortField, setProductSortField] = useState(null);
  const [productSortDirection, setProductSortDirection] = useState("asc");
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [newVendor, setNewVendor] = useState(DEFAULT_NEW_VENDOR);
  const [newProduct, setNewProduct] = useState(DEFAULT_NEW_PRODUCT);
  const [newTransaction, setNewTransaction] = useState(DEFAULT_NEW_TRANSACTION);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [selectedVendorForTransaction, setSelectedVendorForTransaction] = useState(null);
  const [transactionId, setTransactionId] = useState(() => `TRX${Math.floor(Math.random() * 10000)}`);
  const [vendorErrors, setVendorErrors] = useState({});
  const [editVendorErrors, setEditVendorErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteConfirmVendor, setDeleteConfirmVendor] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showEditTransactionForm, setShowEditTransactionForm] = useState(false);
  const [txModalVendorId, setTxModalVendorId] = useState(null);
  const [showTxModal, setShowTxModal] = useState(false);
  const [txFilter, setTxFilter] = useState("all");

  useEffect(() => { fetchVendors(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterOptions]);

  const sortVendorData = (data, column, direction) => {
    return [...data].sort((a, b) => {
      let valueA = "", valueB = "";
      if (column === "name") { valueA = a.name.toLowerCase(); valueB = b.name.toLowerCase(); }
      else if (column === "businessType") { valueA = (a.businessType || "").toLowerCase(); valueB = (b.businessType || "").toLowerCase(); }
      else if (column === "gstId") { valueA = (a.gstId || "").toLowerCase(); valueB = (b.gstId || "").toLowerCase(); }
      return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };

  const fetchExpiringContracts = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await fetch(API_ENDPOINTS.vendorsExpiringContracts, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setExpiringContracts(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching expiring contracts:", err);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendors, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to fetch vendors: ${response.statusText}`);
      const data = await response.json();
      setVendors(sortVendorData(data.data || [], sortColumn, sortDirection));
      fetchExpiringContracts();
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVendor = async () => {
    if (!editingVendor?.id) return;
    const processed = { ...editingVendor };
    if (processed.businessType === "Other" && processed.otherBusinessType?.trim()) {
      processed.businessType = processed.otherBusinessType.trim();
    }
    const errors = validateVendor(processed);
    if (Object.keys(errors).length > 0) {
      setEditVendorErrors(errors);
      return;
    }
    setEditVendorErrors({});
    const { otherBusinessType: _o, ...dataToSubmit } = processed;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendor(editingVendor.id), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(dataToSubmit),
      });
      if (!response.ok) throw new Error(`Failed to update vendor: ${response.statusText}`);
      const data = await response.json();
      setVendors(vendors.map((v) => (v.id === editingVendor.id ? data.data : v)));
      setShowEditForm(false);
      setEditingVendor(null);
      setEditVendorErrors({});
      setMessage({ type: "success", content: "Vendor updated successfully!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to update vendor: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async () => {
    const processed = { ...newVendor };
    if (processed.businessType === "Other" && processed.otherBusinessType?.trim()) {
      processed.businessType = processed.otherBusinessType.trim();
    }
    const errors = validateVendor(processed);
    if (Object.keys(errors).length > 0) {
      setVendorErrors(errors);
      return;
    }
    setVendorErrors({});
    const { otherBusinessType: _o, ...dataToSubmit } = processed;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendors, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(dataToSubmit),
      });
      const respBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        // If the add-vendor modal is open, surface the server error inside the modal
        const serverMsg = respBody?.message || respBody?.error || response.statusText || "Failed to add vendor";
        if (showVendorForm) {
          setVendorErrors((prev) => ({ ...prev, _form: serverMsg, _existing: respBody?.existing || null }));
          return;
        }
        throw new Error(serverMsg || `Failed to add vendor: ${response.statusText}`);
      }
      const data = respBody;
      setVendors([...vendors, data.data]);
      setShowVendorForm(false);
      setNewVendor(DEFAULT_NEW_VENDOR);
      setVendorErrors({});
      setMessage({ type: "success", content: "Vendor added successfully!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to add vendor: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (selectedVendor === "all") {
      setMessage({ type: "error", content: "Please select a vendor first" });
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendorProducts(selectedVendor), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...newProduct, vendorId: selectedVendor }),
      });
      if (!response.ok) throw new Error(`Failed to add product: ${response.statusText}`);
      const data = await response.json();
      setVendors(vendors.map((vendor) => vendor.id.toString() === selectedVendor ? data.data : vendor));
      setShowAddForm(false);
      setNewProduct(DEFAULT_NEW_PRODUCT);
      setMessage({ type: "success", content: "Product added successfully!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to add product: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!selectedVendorForTransaction) {
      setMessage({ type: "error", content: "Please select a vendor first" });
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendorTransactions(selectedVendorForTransaction.id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newTransaction),
      });
      if (!response.ok) throw new Error(`Failed to add transaction: ${response.statusText}`);
      const data = await response.json();
      setVendors(vendors.map((vendor) => vendor.id === selectedVendorForTransaction.id ? data.data : vendor));
      setShowTransactionForm(false);
      setNewTransaction(DEFAULT_NEW_TRANSACTION);
      setTransactionId(`TRX${Math.floor(Math.random() * 10000)}`);
      setMessage({ type: "success", content: "Transaction added successfully!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to add transaction: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const updateTransactionStatus = async (vendorId, transactionId, status) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const vendor = vendors.find((v) => v.id === vendorId);
      if (!vendor) throw new Error("Vendor not found");

      const updatedTransactions = vendor.transactions.map((t) =>
        t.id === transactionId ? { ...t, status } : t
      );

      const response = await fetch(API_ENDPOINTS.vendor(vendorId), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...vendor, transactions: updatedTransactions }),
      });
      if (!response.ok) throw new Error(`Failed to update transaction status: ${response.statusText}`);
      const data = await response.json();
      setVendors(vendors.map((v) => (v.id === vendorId ? data.data : v)));
      setMessage({ type: "success", content: "Transaction status updated successfully!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to update transaction status: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const toggleVendorStatus = async (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    if (!vendor) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendor(vendorId), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: vendor.status === "active" ? "inactive" : "active" }),
      });
      if (!response.ok) throw new Error(`Failed to update vendor status: ${response.statusText}`);
      const data = await response.json();
      setVendors(vendors.map((v) => (v.id === vendorId ? data.data : v)));
      setMessage({ type: "success", content: `Vendor status updated to ${vendor.status === "active" ? "inactive" : "active"}` });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to update vendor status: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const initiateDelete = (vendor) => {
    setVendors((prev) => prev.filter((v) => v.id !== vendor.id));
    setDeleteConfirmVendor(null);
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        await fetch(API_ENDPOINTS.vendor(vendor.id), { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
        setPendingDelete(null);
      } catch (err) {
        setVendors((prev) => [...prev, vendor]);
        setMessage({ type: "error", content: `Failed to delete vendor: ${err.message}` });
        setPendingDelete(null);
      }
    }, 5000);
    setPendingDelete({ vendor, timer });
    setMessage({ type: "undo", content: `"${vendor.name}" deleted.`, onUndo: null });
  };

  const undoDelete = () => {
    if (!pendingDelete) return;
    clearTimeout(pendingDelete.timer);
    setVendors((prev) => [...prev, pendingDelete.vendor]);
    setMessage({ type: "success", content: `"${pendingDelete.vendor.name}" restored.` });
    setPendingDelete(null);
  };

  const handleDeleteVendor = (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    if (!vendor) return;
    const unsettled = vendor.transactions
      .filter((t) => t.status === "pending" || t.status === "partial")
      .reduce((sum, t) => sum + t.amount, 0);
    if (unsettled > 0) {
      setDeleteConfirmVendor({ vendor, unsettled });
    } else {
      initiateDelete(vendor);
    }
  };

  const updateTransaction = async (vendorId, transactionId, updatedData) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    if (!vendor) return;
    const updatedTransactions = vendor.transactions.map((t) =>
      t.id === transactionId ? { ...t, ...updatedData } : t
    );
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");
      const response = await fetch(API_ENDPOINTS.vendor(vendorId), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ transactions: updatedTransactions }),
      });
      if (!response.ok) throw new Error(`Failed to update transaction`);
      const data = await response.json();
      setVendors((prev) => prev.map((v) => (v.id === vendorId ? data.data : v)));
      if (txModalVendorId === vendorId) setTxModalVendorId(vendorId);
      setShowEditTransactionForm(false);
      setEditingTransaction(null);
      setMessage({ type: "success", content: "Transaction updated!" });
    } catch (err) {
      setMessage({ type: "error", content: `Failed to update transaction: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const openTxModal = (vendor) => {
    setTxModalVendorId(vendor.id);
    setTxFilter("all");
    setShowTxModal(true);
  };

  const handleVendorSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
    setVendors(sortVendorData(vendors, column, newDirection));
  };

  const handleProductSort = (field) => {
    const newDirection = productSortField === field && productSortDirection === "asc" ? "desc" : "asc";
    setProductSortField(field);
    setProductSortDirection(newDirection);
  };

  const openVendorAgreement = (vendor) => {
    const url = typeof vendor?.agreementFile === "string" ? vendor.agreementFile.trim() : "";
    if (!url) {
      setMessage({ type: "error", content: "No agreement or contract document is on file for this vendor. You can add a document URL when editing the vendor." });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openRecordPurchaseForVendor = (vendor) => {
    setSelectedVendorForTransaction(vendor);
    setShowTransactionForm(true);
  };

  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.gstId && vendor.gstId.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesBusinessType = filterOptions.businessType === "all" || vendor.businessType === filterOptions.businessType;
    const matchesStatus = filterOptions.status === "all" || vendor.status === filterOptions.status;
    return matchesSearch && matchesBusinessType && matchesStatus;
  });

  const paginatedVendors = filteredVendors.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getFilteredProducts = () => {
    const relevantVendors = selectedVendor === "all" ? vendors : vendors.filter((v) => v.id.toString() === selectedVendor);
    const products = relevantVendors.flatMap((vendor) =>
      vendor.products
        .filter((p) => p.productName.toLowerCase().includes(searchTerm.toLowerCase()) || p.vendorId.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((p) => ({ ...p, vendorName: vendor.name, vendorId: vendor.id }))
    );
    const grouped = products.reduce((acc, p) => {
      const key = `${p.vendorId}-${p.buyDate}`;
      if (!acc[key]) acc[key] = { ...p, quantity: 0, totalCost: 0 };
      acc[key].quantity += p.quantity;
      acc[key].totalCost += p.costPrice * p.quantity;
      return acc;
    }, {});
    let sorted = Object.values(grouped);
    if (productSortField) {
      sorted.sort((a, b) => {
        let va = a[productSortField], vb = b[productSortField];
        if (typeof va === "string") { va = va.toLowerCase(); vb = vb.toLowerCase(); }
        if (va < vb) return productSortDirection === "asc" ? -1 : 1;
        if (va > vb) return productSortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  };

  const getReportData = () => ({
    paymentReport: vendors.map((vendor) => ({
      vendorId: vendor.id,
      vendorName: vendor.name,
      totalPaid: vendor.transactions.filter((t) => t.status === "paid").reduce((sum, t) => sum + t.amount, 0) + vendor.transactions.filter((t) => t.status === "partial").reduce((sum, t) => sum + (t.paid_amount || 0), 0),
      totalPartial: vendor.transactions.filter((t) => t.status === "partial").reduce((sum, t) => sum + (t.amount - (t.paid_amount || 0)), 0),
      totalPending: vendor.transactions.filter((t) => t.status === "pending").reduce((sum, t) => sum + t.amount, 0),
      lastTransactionDate: vendor.transactions.length > 0
        ? new Date(Math.max(...vendor.transactions.map((t) => new Date(t.date)))).toLocaleDateString()
        : "No transactions",
    })),
  });

  const getAlerts = () => {
    const alerts = [];
    expiringContracts.forEach((vendor) => {
      if (vendor.contractExpiry) {
        const daysUntilExpiry = Math.ceil((new Date(vendor.contractExpiry) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry > 0 && daysUntilExpiry <= 30) {
          alerts.push({ type: "contract", vendorName: vendor.name, message: `Contract expires in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? "day" : "days"}`, severity: daysUntilExpiry <= 7 ? "high" : "medium" });
        }
      }
    });
    vendors.forEach((vendor) => {
      const pendingAmount = vendor.transactions.filter((t) => t.status === "pending").reduce((sum, t) => sum + t.amount, 0);
      if (pendingAmount > 0) {
        alerts.push({ type: "payment", vendorName: vendor.name, message: `Has pending payments of ₹${pendingAmount.toLocaleString()}`, severity: pendingAmount > 50000 ? "high" : "medium" });
      }
    });
    return alerts.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.severity] !== order[b.severity] ? order[a.severity] - order[b.severity] : a.type.localeCompare(b.type);
    });
  };

  const getVendorPerformance = () =>
    vendors
      .filter((v) => v.status === "active" && v.transactions.length > 0)
      .map((vendor) => {
        const total = vendor.transactions.length;
        const onTime = vendor.transactions.filter((t) => t.deliveryOnTime === true).length;
        return {
          vendorId: vendor.id,
          vendorName: vendor.name,
          onTimePercentage: total > 0 ? Math.round((onTime / total) * 100) : 0,
          qualityRating: vendor.performance?.qualityRating || 0,
          totalTransactions: total,
        };
      })
      .sort((a, b) => b.onTimePercentage - a.onTimePercentage)
      .slice(0, 5);

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium",
      inactive: "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium",
      paid: "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium",
      pending: "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium",
      partial: "bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium",
    };
    return classes[status] || "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium";
  };

  return {
    vendors, loading, error, message, setMessage,
    selectedVendor, setSelectedVendor,
    searchTerm, setSearchTerm,
    showAddForm, setShowAddForm,
    showVendorForm, setShowVendorForm,
    currentView, setCurrentView,
    filterOptions, setFilterOptions,
    editingVendor, setEditingVendor,
    showEditForm, setShowEditForm,
    sortColumn, sortDirection,
    currentPage, setCurrentPage,
    itemsPerPage, setItemsPerPage,
    productSortField, productSortDirection,
    expiringContracts,
    newVendor, setNewVendor,
    newProduct, setNewProduct,
    newTransaction, setNewTransaction,
    showTransactionForm, setShowTransactionForm,
    selectedVendorForTransaction, setSelectedVendorForTransaction,
    transactionId,
    vendorErrors, setVendorErrors,
    editVendorErrors, setEditVendorErrors,
    pendingDelete, undoDelete,
    deleteConfirmVendor, setDeleteConfirmVendor, initiateDelete,
    editingTransaction, setEditingTransaction,
    showEditTransactionForm, setShowEditTransactionForm,
    txModalVendorId, showTxModal, setShowTxModal,
    txFilter, setTxFilter,
    filteredVendors, paginatedVendors,
    fetchVendors,
    handleEditVendor, handleAddVendor, handleAddProduct,
    handleAddTransaction, toggleVendorStatus, handleDeleteVendor,
    updateTransaction, openTxModal,
    handleVendorSort, handleProductSort,
    openVendorAgreement, openRecordPurchaseForVendor,
    getFilteredProducts, getReportData, getAlerts, getVendorPerformance,
    getStatusBadgeClass, updateTransactionStatus,
  };
}
